"use client";

import { useEffect, useState } from "react";
import { Link } from "@frontend/lib/link";
import {
  acceptOfferAction,
  approveDraftAction,
  cancelCollabAction,
  confirmLiveAction,
  confirmPaymentAction,
  rejectPaymentAction,
  requestRevisionAction,
  submitDraftAction,
  submitLiveUrlAction,
} from "@frontend/lib/actions";
import { ActionForm, SubmitButton } from "@frontend/components/action-form";
import { CountdownPill } from "@frontend/components/countdown";
import { CounterOfferDialog } from "@frontend/components/collab/counter-offer-dialog";
import { PayNowDialog, type PayMethodView } from "@frontend/components/collab/pay-now-dialog";
import { ProofRow } from "@frontend/components/collab/payment-proof";
import {
  Disclosure,
  Field,
  LinkButton,
  Money,
  OptionalField,
  cx,
} from "@frontend/components/ui";
import { canCancelCollab, hasNonRefundablePayment } from "@frontend/lib/cancel";
import { payMethodsFor } from "@frontend/lib/collab-pay-methods";
import type { ActiveTimer, CollabRoom } from "@creators/backend/lib/collabs/queries";
import { formatINR } from "@creators/backend/lib/money";
import {
  effectiveCollabStatus,
  type CollabStatus,
} from "@creators/backend/lib/collabs/state-machine";

type Proof = CollabRoom["proofs"][number];

type Props = {
  room: CollabRoom;
  viewer: "creator" | "brand" | "admin";
  verified: boolean;
  canAct: boolean;
  /** The deadline currently running on this status, if any. */
  timer: ActiveTimer;
  className?: string;
};

/**
 * One card at the top of the sidebar for whatever needs doing right now —
 * accept, pay, confirm receipt, submit work, review a draft, and so on.
 * Status panels below stay read-only.
 */
export function CollabActionBox({ room, viewer, verified, canAct, timer, className }: Props) {
  const rawStatus = room.collab.status as CollabStatus;
  const status = effectiveCollabStatus(rawStatus, room.milestones);
  const offer = room.currentOffer;

  const isCreator = viewer === "creator";
  const dueMilestones = room.milestones.filter((m) => m.status === "due");
  const paidMilestones = room.milestones.filter((m) => m.status === "paid");
  const dueMilestone = dueMilestones[0] ?? null;
  const paidMilestone = paidMilestones[0] ?? null;
  const dueAmountTotal = dueMilestones.reduce((sum, m) => sum + m.amount, 0);
  const paidAmountTotal = paidMilestones.reduce((sum, m) => sum + m.amount, 0);
  // Confirming receipt is a judgement about evidence, so the evidence travels
  // with the action instead of sitting in the payments panel further down.
  const paidProof = paidMilestone
    ? (room.proofs
        .filter((p) => p.milestoneId === paidMilestone.id && p.status === "pending")
        .at(-1) ?? null)
    : null;
  const paymentDue = dueMilestones.length > 0;
  const paymentOutstanding = room.milestones.some((m) => m.status === "due" || m.status === "paid");
  // Admins observe. Completed is quiet unless money is still moving (paid → confirm).
  const actionable =
    viewer !== "admin" &&
    (status !== "completed" || paymentDue || paymentOutstanding || paidMilestones.length > 0);
  const needsPayMethods =
    Boolean(offer) &&
    actionable &&
    !isCreator &&
    dueMilestones.length > 0 &&
    Boolean(room.payoutDetails);

  const [payMethods, setPayMethods] = useState<PayMethodView[]>([]);
  const dueAmount = dueMilestones.length > 0 ? dueAmountTotal : null;
  const payoutKey = room.payoutDetails
    ? [
        room.payoutDetails.defaultMethod,
        room.payoutDetails.upiId,
        room.payoutDetails.bankAccountNumber,
        room.payoutDetails.bankIfsc,
        room.payoutDetails.otherPayoutDetails,
      ].join("|")
    : "";

  useEffect(() => {
    if (!needsPayMethods || dueAmount == null) {
      setPayMethods([]);
      return;
    }
    let cancelled = false;
    void payMethodsFor(room, dueAmount).then((methods) => {
      if (!cancelled) setPayMethods(methods);
    });
    return () => {
      cancelled = true;
    };
    // room is read for payout/creator fields; payoutKey + dueAmount gate reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid regenerating QR on every sync
  }, [needsPayMethods, dueAmount, payoutKey, room.collab.id, room.collab.reference, room.creator.displayName]);

  if (!offer) return null;

  const canAccept =
    canAct &&
    (status === "offer_pending" || status === "negotiating") &&
    offer.createdBy !== viewer;
  const needsPayoutDetails =
    isCreator && offer.compensationType !== "barter" && !room.payoutDetails;

  const content = actionable
    ? resolveActionContent({
        room,
        status,
        isCreator,
        verified,
        canAct,
        canAccept,
        needsPayoutDetails,
        paymentDue,
        paymentOutstanding,
        dueMilestones,
        paidMilestones,
        dueAmountTotal,
        paidAmountTotal,
        paidProof,
        payMethods,
      })
    : null;

  // Never leave Details empty while money is waiting — fall back to a clear card.
  const fallback =
    !content &&
    (status === "awaiting_payment" || paymentDue || paidMilestones.length > 0)
      ? {
          title: paymentDue
            ? isCreator
              ? "Waiting on payment"
              : "Send payment"
            : paidMilestones.length > 0
              ? isCreator
                ? "Confirm payment"
                : "Payment recorded"
              : isCreator
                ? "Waiting for the advance"
                : "Send the advance to start",
          hint: paymentDue
            ? `${formatINR(dueAmountTotal)} is due now.`
            : paidMilestones.length > 0
              ? `${formatINR(paidAmountTotal)} is marked as sent.`
              : undefined,
          accent: true as const,
          body: (
            <p className="text-sm text-ink-muted">
              {isCreator
                ? "Terms are frozen. Once the advance lands and you confirm it, work can start."
                : "Pay the advance, then record it here so the creator can confirm."}
            </p>
          ),
        }
      : null;

  const shown = content ?? fallback;
  if (!shown && !timer) return null;

  const deadline = timer ? new Date(timer.deadline).toISOString() : null;
  // With no action to take, the deadline is the whole point of the card.
  const title = shown?.title ?? "Deadline";

  return (
    // Black spotlight — the one card that needs acting on. Tokens are remapped
    // via `.action-spotlight` so children stay readable in light and dark themes.
    <section
      className={cx(
        // Stick to the top of the Details scroller so payment waiting never
        // disappears under Agreement when the panel is scrolled.
        "action-spotlight sticky top-0 z-10 shrink-0 overflow-hidden rounded-none border-x-0 border-t-0",
        className,
      )}
    >
      <div className="flex items-start gap-2.5 border-b border-line px-4 py-3">
        <span
          aria-hidden="true"
          className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
            shown?.accent ? "animate-pulse bg-money-600" : "bg-ink-subtle"
          }`}
        />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center justify-between gap-3">
            <h2 className="min-w-0 truncate text-sm font-semibold tracking-tight text-ink">
              {title}
            </h2>
            {timer && deadline ? (
              <CountdownPill
                deadline={deadline}
                initialLabel={timer.initialLabel}
                action={timer.label}
                tone="amber"
              />
            ) : null}
          </div>
          {shown?.hint ? (
            <p className="text-xs font-medium text-ink-muted">{shown.hint}</p>
          ) : null}
        </div>
      </div>
      <div className="space-y-3 px-4 py-3.5">
        {shown?.body ?? (
          <p className="text-sm leading-relaxed text-ink-muted">{timer?.consequence}</p>
        )}
      </div>
    </section>
  );
}

type ActionContent = {
  title: string;
  hint?: string;
  accent: boolean;
  body: React.ReactNode;
};

function resolveActionContent(input: {
  room: CollabRoom;
  status: CollabStatus;
  isCreator: boolean;
  verified: boolean;
  canAct: boolean;
  canAccept: boolean;
  needsPayoutDetails: boolean;
  paymentDue: boolean;
  paymentOutstanding: boolean;
  dueMilestones: CollabRoom["milestones"];
  paidMilestones: CollabRoom["milestones"];
  dueAmountTotal: number;
  paidAmountTotal: number;
  paidProof: Proof | null;
  payMethods: PayMethodView[];
}): ActionContent | null {
  const {
    room,
    status,
    isCreator,
    verified,
    canAct,
    canAccept,
    needsPayoutDetails,
    paymentDue,
    paymentOutstanding,
    dueMilestones,
    paidMilestones,
    dueAmountTotal,
    paidAmountTotal,
    paidProof,
    payMethods,
  } = input;

  // Money actions beat workflow actions — except final work review, which must
  // happen before the collaborator is asked to pay remaining content dues.
  if (status === "published" && !isCreator && canAct) {
    const pub = room.latestPublished;
    const urls = [
      ...(pub?.publishedUrl ? [pub.publishedUrl] : []),
      ...((pub?.assetUrls ?? []) as string[]),
    ];
    return {
      title: "Review final work",
      accent: true,
      body: (
        <>
          <SubmissionLinks
            urls={urls}
            note={pub?.note}
            emptyHint="No link or note was attached."
          />
          {verified ? (
            <>
              <ActionForm action={confirmLiveAction}>
                <input type="hidden" name="collabId" value={room.collab.id} />
                <SubmitButton size="lg" variant="money" full pendingLabel="Confirming…">
                  Approve final work
                </SubmitButton>
              </ActionForm>

              <ActionForm action={requestRevisionAction}>
                <input type="hidden" name="collabId" value={room.collab.id} />
                <SubmitButton variant="secondary" full pendingLabel="Sending…">
                  Request revision
                </SubmitButton>
              </ActionForm>
            </>
          ) : (
            <ConfirmSignIn collabId={room.collab.id} action="approve or send back this work" />
          )}
        </>
      ),
    };
  }

  if (canAct && isCreator && paidMilestones.length > 0) {
    return {
      title: "Confirm payment",
      hint: `${formatINR(paidAmountTotal)} is marked as sent.`,
      accent: true,
      body: (
        <ConfirmReceipt
          collabId={room.collab.id}
          milestoneIds={paidMilestones.map((m) => m.id)}
          proof={paidProof}
        />
      ),
    };
  }

  // Revision / draft review beat content payment — pay only after work is approved.
  if ((status === "in_progress" || status === "revision_requested") && isCreator && canAct) {
    return {
      title: status === "revision_requested" ? "Submit revised work" : "Submit your work",
      hint:
        room.revisions.at(-1)?.note?.trim() ||
        (status === "revision_requested"
          ? "Check chat for what needs to change."
          : "Draft for review, or final if live."),
      accent: true,
      body: <SubmitWorkForm collabId={room.collab.id} />,
    };
  }

  if (status === "revision_requested" && !isCreator) {
    return {
      title: "Waiting for revision",
      accent: false,
      body: (
        <p className="text-sm text-ink-muted">
          The creator is working on your feedback. You will be notified when the revised work is
          ready.
        </p>
      ),
    };
  }

  if (status === "draft_submitted" && !isCreator && canAct) {
    return {
      title: "Review the work",
      accent: true,
      body: (
        <>
          <SubmissionLinks
            urls={(room.latestDraft?.assetUrls ?? []) as string[]}
            note={room.latestDraft?.note}
            emptyHint="No link was attached — check the note, or ask them to resubmit."
          />
          {verified ? (
            <>
              <ActionForm action={approveDraftAction}>
                <input type="hidden" name="collabId" value={room.collab.id} />
                <SubmitButton size="lg" variant="money" full pendingLabel="Approving…">
                  Approve work
                </SubmitButton>
              </ActionForm>

              <ActionForm action={requestRevisionAction}>
                <input type="hidden" name="collabId" value={room.collab.id} />
                <SubmitButton variant="secondary" full pendingLabel="Sending…">
                  Request revision
                </SubmitButton>
              </ActionForm>
            </>
          ) : (
            <ConfirmSignIn collabId={room.collab.id} action="approve or send back this work" />
          )}
        </>
      ),
    };
  }

  if (status === "draft_submitted" && isCreator) {
    return {
      title: "Work is with them",
      accent: false,
      body: (
        <div className="space-y-2">
          <SubmissionLinks
            urls={(room.latestDraft?.assetUrls ?? []) as string[]}
            note={room.latestDraft?.note}
          />
          <p className="text-sm text-ink-muted">
            Approved automatically if they do not respond in time.
          </p>
        </div>
      ),
    };
  }

  if (!isCreator && dueMilestones.length > 0) {
    return {
      title: "Send payment",
      hint: `${formatINR(dueAmountTotal)} is due now.`,
      accent: true,
      body: !canAct ? (
        <p className="text-sm text-ink-muted">
          You can view this collab but not record payment from this account.
        </p>
      ) : verified ? (
        room.payoutDetails ? (
          <PayNowDialog
            collabId={room.collab.id}
            milestoneIds={dueMilestones.map((m) => m.id)}
            amountPaise={dueAmountTotal}
            creatorName={room.creator.displayName}
            triggerLabel={`Pay ${formatINR(dueAmountTotal)}`}
            methods={payMethods}
          />
        ) : (
          <p className="text-sm text-ink-muted">
            {room.creator.displayName} has not added payment details yet. Ask them to add a method
            before you send anything.
          </p>
        )
      ) : (
        <ConfirmSignIn collabId={room.collab.id} action="record a payment" />
      ),
    };
  }

  if (status === "awaiting_payment" && isCreator) {
    return {
      title: "Waiting for the advance",
      hint: dueMilestones.length > 0 ? `${formatINR(dueAmountTotal)} is due from them.` : undefined,
      accent: false,
      body: (
        <p className="text-sm text-ink-muted">
          Terms are frozen. Once you confirm the advance has landed, the clock starts on your draft.
        </p>
      ),
    };
  }

  if ((status === "offer_pending" || status === "negotiating") && canAccept) {
    // Creators receive brand offers as-is — Accept / Decline only.
    const canCounter = !isCreator && room.currentOffer?.compensationType !== "barter";
    return {
      title: "Your move",
      hint:
        room.currentOffer?.compensationType === "barter"
          ? room.currentOffer.barterDescription ?? "Barter offer waiting."
          : undefined,
      accent: true,
      body: (
        <>
          {needsPayoutDetails ? (
            <Link
              href="/settings/payments"
              className="flex items-center gap-2.5 rounded-xl border border-warn-100 bg-warn-50 px-3 py-2.5 transition hover:bg-warn-100/60"
            >
              <span className="min-w-0 flex-1 text-sm font-medium text-warn-700">
                Add payout to accept
              </span>
              <span className="shrink-0 text-sm font-semibold text-warn-700">Add</span>
            </Link>
          ) : null}

          <ActionForm action={acceptOfferAction}>
            <input type="hidden" name="collabId" value={room.collab.id} />
            <SubmitButton size="sm" full pendingLabel="Accepting…" disabled={needsPayoutDetails}>
              Accept
            </SubmitButton>
          </ActionForm>

          {canCounter ? <CounterOfferDialog room={room} /> : null}

          {canCancelCollab({
            side: isCreator ? "creator" : "brand",
            milestones: room.milestones,
          }) ? (
            <CancelDisclosure
              collabId={room.collab.id}
              label={isCreator ? "Decline this offer" : "Withdraw from this collab"}
              verified={verified}
              moneyMoved={hasNonRefundablePayment(room.milestones)}
            />
          ) : null}
        </>
      ),
    };
  }

  if (status === "draft_approved" && isCreator && canAct && !paymentOutstanding) {
    return {
      title: "Publish and share the link",
      accent: true,
      body: (
        <ActionForm action={submitLiveUrlAction}>
          <input type="hidden" name="collabId" value={room.collab.id} />
          <Field label="Link to the live post">
            <input
              name="publishedUrl"
              required
              placeholder="https://instagram.com/reel/…"
              className="field-input"
            />
          </Field>
          <SubmitButton size="lg" full pendingLabel="Submitting…">
            Complete work
          </SubmitButton>
        </ActionForm>
      ),
    };
  }

  // Negotiating but waiting on the other side — still one card, no form.
  if ((status === "offer_pending" || status === "negotiating") && canAct) {
    return {
      title: "Waiting on them",
      accent: false,
      body: (
        <>
          <p className="text-sm text-ink-muted">
            With the {isCreator ? "collaborator" : "creator"} — you can revise your counter anytime.
          </p>
          <CounterOfferDialog room={room} />
          {canCancelCollab({
            side: isCreator ? "creator" : "brand",
            milestones: room.milestones,
          }) ? (
            <CancelDisclosure
              collabId={room.collab.id}
              label="Withdraw from this collab"
              verified={verified}
              moneyMoved={hasNonRefundablePayment(room.milestones)}
            />
          ) : null}
        </>
      ),
    };
  }

  // Informational cards when nothing actionable for this side.
  const waiting = waitingCopy({
    status,
    isCreator,
    paymentDue,
    paymentOutstanding,
    paidMilestone: paidMilestones[0] ?? null,
    paidAmountTotal,
    paidProof,
    room,
  });

  if (!waiting) return null;

  return {
    title: waiting.title,
    accent: false,
    body: waiting.body,
  };
}

function waitingCopy(input: {
  status: CollabStatus;
  isCreator: boolean;
  paymentDue: boolean;
  paymentOutstanding: boolean;
  paidMilestone: CollabRoom["milestones"][number] | null;
  paidAmountTotal: number;
  paidProof: Proof | null;
  room: CollabRoom;
}): { title: string; body: React.ReactNode } | null {
  const {
    status,
    isCreator,
    paymentDue,
    paymentOutstanding,
    paidMilestone,
    paidAmountTotal,
    paidProof,
    room,
  } = input;

  if (status === "awaiting_payment") {
    return {
      title: isCreator ? "Waiting for the advance" : "Send the advance to start",
      body: (
        <p className="text-sm text-ink-muted">
          {isCreator
            ? "Terms are frozen. Once you confirm the advance has landed, the clock starts on your draft."
            : "Send the advance using the payment details below, then record it in the action box when it appears."}
        </p>
      ),
    };
  }

  if (status === "in_progress" && !isCreator) {
    return {
      title: "Creator is working",
      body: (
        <p className="text-sm text-ink-muted">
          You will get the draft to review before anything goes live.
        </p>
      ),
    };
  }

  if (status === "draft_approved" && isCreator && paymentOutstanding) {
    return {
      title: paymentDue ? "Waiting for payment" : "Confirm payment first",
      body: (
        <p className="text-sm text-ink-muted">
          {paymentDue
            ? "They need to pay the due tranche first. Once you confirm it landed, you can publish."
            : paidMilestone
              ? "They say they have paid. Confirm it in the action box when you are ready."
              : "Waiting on payment before you can publish."}
        </p>
      ),
    };
  }

  if (status === "draft_approved" && !isCreator) {
    return {
      title: paymentOutstanding ? "Payment due" : "Approved",
      body: (
        <p className="text-sm text-ink-muted">
          {paymentDue
            ? "Pay the due tranche in the action box above when you are ready. The creator publishes after confirming."
            : paymentOutstanding
              ? "Waiting on the creator to confirm your payment."
              : "The draft is signed off. The creator will post and share the live link."}
        </p>
      ),
    };
  }

  if (status === "published" && isCreator) {
    const pub = room.latestPublished;
    const urls = [
      ...(pub?.publishedUrl ? [pub.publishedUrl] : []),
      ...((pub?.assetUrls ?? []) as string[]),
    ];
    return {
      title: "Waiting for work confirmation",
      body: (
        <div className="space-y-2">
          <SubmissionLinks urls={urls} note={pub?.note} />
          <p className="text-sm text-ink-muted">
            They approve your final work, or it confirms automatically if they do not respond in
            time.
          </p>
        </div>
      ),
    };
  }

  if (status === "cancelled" || status === "expired") {
    return {
      title: status === "expired" ? "Expired" : "Cancelled",
      body: (
        <p className="text-sm text-ink-muted">
          {status === "expired"
            ? "This offer lapsed without a response. Either side can start a fresh one."
            : "This collaboration ended. Anything already paid is between the two of you to settle."}
        </p>
      ),
    };
  }

  if (!isCreator && paidMilestone) {
    return {
      title: "Payment recorded",
      body: (
        <div className="space-y-2">
          <p className="text-sm text-ink-muted">
            Waiting on {room.creator.displayName} to confirm{" "}
            <Money paise={paidAmountTotal} size="sm" className="text-ink" /> landed.
          </p>
          {paidProof ? <ProofRow collabId={room.collab.id} proof={paidProof} /> : null}
        </div>
      ),
    };
  }

  return null;
}

function ConfirmReceipt({
  collabId,
  milestoneIds,
  proof,
}: {
  collabId: string;
  milestoneIds: string[];
  proof: Proof | null;
}) {
  const ids = milestoneIds.join(",");
  return (
    <div className="space-y-2">
      {proof ? <ProofRow collabId={collabId} proof={proof} /> : null}

      <ActionForm action={confirmPaymentAction}>
        <input type="hidden" name="collabId" value={collabId} />
        <input type="hidden" name="milestoneIds" value={ids} />
        <SubmitButton size="lg" variant="money" full pendingLabel="Confirming…">
          Confirm the money arrived
        </SubmitButton>
      </ActionForm>

      <Disclosure label="It has not arrived" icon={<AlertIcon />} tone="danger">
        <ActionForm action={rejectPaymentAction}>
          <input type="hidden" name="collabId" value={collabId} />
          <input type="hidden" name="milestoneIds" value={ids} />
          <Field label="What is wrong?" hint="They can send it again with a clearer note.">
            <textarea
              name="reason"
              rows={2}
              required
              placeholder="Nothing has shown up in my account."
              className="field-input resize-y"
            />
          </Field>
          <SubmitButton variant="danger" full pendingLabel="Sending…">
            Flag not received
          </SubmitButton>
        </ActionForm>
      </Disclosure>
    </div>
  );
}

function SubmissionLinks({
  urls,
  note,
  emptyHint,
}: {
  urls: string[];
  note?: string | null;
  emptyHint?: string;
}) {
  const links = urls.filter(Boolean);
  if (links.length === 0 && !note) {
    return emptyHint ? <p className="text-sm text-ink-muted">{emptyHint}</p> : null;
  }

  return (
    <ul className="space-y-1.5">
      {links.map((url) => (
        <li key={url}>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block truncate rounded-xl border border-line bg-canvas px-3.5 py-2.5 text-sm text-brand-700 hover:bg-brand-50"
          >
            {url}
          </a>
        </li>
      ))}
      {note ? (
        <li className="space-y-1 rounded-xl border border-line bg-canvas px-3.5 py-2.5">
          <p className="text-xs font-medium text-ink-subtle">Note</p>
          <p className="text-sm text-ink">{note}</p>
        </li>
      ) : null}
    </ul>
  );
}

/** Draft for review, or live post when the work is already final. */
function SubmitWorkForm({ collabId }: { collabId: string }) {
  const [mode, setMode] = useState<"draft" | "live">("draft");

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-canvas p-1">
        {(
          [
            { id: "draft", label: "Draft" },
            { id: "live", label: "Final" },
          ] as const
        ).map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setMode(opt.id)}
            className={cx(
              "rounded-lg px-2 py-2 text-sm font-medium transition",
              mode === opt.id
                ? "bg-surface text-ink shadow-sm"
                : "text-ink-muted hover:text-ink",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {mode === "draft" ? (
        <ActionForm action={submitDraftAction}>
          <input type="hidden" name="collabId" value={collabId} />
          <Field
            label="Link to the draft"
            hint="Google Drive, Dropbox or any shareable link. One per line."
          >
            <textarea
              name="assetUrls"
              rows={2}
              placeholder="https://drive.google.com/…"
              className="field-input resize-y"
            />
          </Field>
          <OptionalField label="Note for them" addLabel="Add a note">
            <textarea name="note" rows={2} className="field-input resize-y" />
          </OptionalField>
          <SubmitButton size="lg" full pendingLabel="Submitting…">
            Submit for review
          </SubmitButton>
        </ActionForm>
      ) : (
        <ActionForm action={submitLiveUrlAction}>
          <input type="hidden" name="collabId" value={collabId} />
          <input type="hidden" name="asFinal" value="1" />
          <Field
            label="Link to the final post"
            hint="Add a link or a note. Clear due payments first."
          >
            <input
              name="publishedUrl"
              placeholder="https://instagram.com/reel/…"
              className="field-input"
            />
          </Field>
          <OptionalField label="Note for them" addLabel="Add a note">
            <textarea name="note" rows={2} className="field-input resize-y" />
          </OptionalField>
          <SubmitButton size="lg" full pendingLabel="Submitting…">
            Submit final work
          </SubmitButton>
        </ActionForm>
      )}
    </div>
  );
}

function ConfirmSignIn({ collabId, action }: { collabId: string; action: string }) {
  return (
    <div className="rounded-xl border border-line bg-canvas p-4">
      <p className="text-sm text-ink-muted">Sign in with WhatsApp to {action}.</p>
      <div className="mt-3">
        <LinkButton href={`/login?next=/collabs/${collabId}`} full>
          Sign in with WhatsApp
        </LinkButton>
      </div>
    </div>
  );
}

function CancelDisclosure({
  collabId,
  label,
  verified,
  moneyMoved,
}: {
  collabId: string;
  label: string;
  verified: boolean;
  moneyMoved: boolean;
}) {
  return (
    <div className="border-t border-line pt-2">
      <Disclosure label={label} icon={<WithdrawIcon />} tone="danger">
        {!verified ? (
          <ConfirmSignIn collabId={collabId} action="end this collab" />
        ) : (
          <ActionForm action={cancelCollabAction}>
            <input type="hidden" name="collabId" value={collabId} />
            <Field
              label="Reason"
              optional
              hint={
                moneyMoved
                  ? "Payments already made are non-refundable and stay with the creator. Unpaid amounts are no longer due."
                  : "Cancelling closes this collab for both sides."
              }
            >
              <textarea
                name="reason"
                rows={2}
                placeholder="Optional — share why if you want"
                className="field-input resize-y"
              />
            </Field>
            <SubmitButton variant="danger" full pendingLabel="Cancelling…">
              Confirm cancellation
            </SubmitButton>
          </ActionForm>
        )}
      </Disclosure>
    </div>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11" r="0.75" fill="currentColor" />
    </svg>
  );
}

function WithdrawIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="size-4" aria-hidden="true">
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.75 8h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
